import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Quote, Heart, User, MapPin, Mail, Sparkles, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = ["Général", "Conversion", "Guérison", "Délivrance", "Restauration couple/famille", "Provision"];

const DEMO_TESTIMONIES = [
  {
    id: "d1",
    title: "Il m'a rencontré dans mon silence",
    name: "Sarah, 32 ans",
    city: "Lyon",
    category: "Conversion",
    story: "J'étais dans le doute depuis des mois. Un épisode de Wake Up It's Time m'a bouleversée. J'ai rendu ma vie à Jésus dans la nuit, à genoux dans ma cuisine. Depuis, tout est différent — pas parfait, mais vivant.",
  },
  {
    id: "d2",
    title: "Guérie d'une blessure de 20 ans",
    name: "Emmanuel",
    city: "Douala",
    category: "Guérison",
    story: "Le parcours La Traversée m'a permis de nommer ma blessure d'abandon. Semaine après semaine, Dieu a défait les nœuds. Aujourd'hui, je pardonne ce que je croyais impardonnable.",
  },
  {
    id: "d3",
    title: "Notre couple restauré",
    name: "Marc & Judith",
    city: "Paris",
    category: "Restauration couple/famille",
    story: "Après l'invitation du prophète Belver dans notre église, nous avons pris rendez-vous ensemble avec le Seigneur. Trois mois plus tard, nos échanges ne ressemblent plus à ceux d'avant.",
  },
];

export default function Testimonies() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", city: "", title: "", story: "", category: "Général" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/testimonies").then((r) => {
      const backend = r.data.testimonies || [];
      setItems(backend.length ? backend : DEMO_TESTIMONIES);
    }).catch(() => setItems(DEMO_TESTIMONIES));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/testimonies", form);
      toast.success(data.message || "Merci pour ton témoignage !");
      setForm({ name: "", email: "", city: "", title: "", story: "", category: "Général" });
      setShowForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-testimonies" className="bg-[#F7F5F0]">
      {/* Hero */}
      <section className="relative py-32 md:py-40 bg-gradient-to-b from-[#4A6D82] to-[#7B98A8] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 jt-grain" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1] flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Vies transformées
          </span>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-4 mb-5">
            Il fait <em className="not-italic text-[#F4E4C1]">toutes choses</em> nouvelles.
          </h1>
          <p className="text-lg text-white/85 max-w-2xl mx-auto">
            Récits concrets de guérisons, de restaurations et de rencontres avec Dieu. Ta victoire est peut-être le début de celle d'un autre.
          </p>
          <button
            onClick={() => setShowForm(true)}
            data-testid="open-testimony-form"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#2E4959] hover:bg-[#F4E4C1] transition jt-medium-shadow"
          >
            <Heart className="w-4 h-4" /> Partager mon témoignage
          </button>
        </div>
      </section>

      {/* Testimonies list */}
      <section className="py-20 md:py-24 max-w-6xl mx-auto px-6" data-testid="testimonies-list">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <article key={t.id} className="rounded-2xl bg-white border border-[#DDE3E8] p-6 jt-soft-shadow flex flex-col" data-testid={`testimony-${t.id}`}>
              <Quote className="w-8 h-8 text-[#C99A5B] mb-4" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-widest text-[#4A6D82] mb-2">{t.category}</span>
              <h3 className="font-serif text-xl text-[#1E2933] leading-tight mb-3">{t.title}</h3>
              <p className="text-sm text-[#1E2933]/75 flex-1 leading-relaxed">{t.story}</p>
              <div className="mt-5 pt-4 border-t border-[#DDE3E8] flex items-center justify-between text-xs text-[#6B7A85]">
                <span className="font-medium text-[#4A6D82]">{t.name}</span>
                {t.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.city}</span>}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Modal form */}
      {showForm && (
        <div
          className="fixed inset-0 z-[60] bg-[#2E4959]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
          data-testid="testimony-modal"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl w-full max-w-2xl p-6 md:p-8 jt-medium-shadow max-h-[92vh] overflow-y-auto"
            data-testid="testimony-form"
          >
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="absolute top-4 right-4 h-9 w-9 rounded-full bg-[#E4EBEF] text-[#4A6D82] hover:bg-[#4A6D82] hover:text-white flex items-center justify-center"
              aria-label="Fermer"
            >
              ×
            </button>
            <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">Partage ton histoire</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E2933] mt-2 mb-6 leading-tight">
              Raconte-nous ce que <em className="not-italic text-[#4A6D82]">Dieu a fait</em>.
            </h2>

            <div className="grid gap-4">
              <div className="grid md:grid-cols-2 gap-4">
                <FormField icon={<User className="w-4 h-4" />} label="Ton prénom" required>
                  <input required data-testid="test-name" value={form.name} onChange={set("name")} placeholder="Sarah" className="w-full bg-transparent focus:outline-none" />
                </FormField>
                <FormField icon={<Mail className="w-4 h-4" />} label="Email (optionnel)">
                  <input type="email" data-testid="test-email" value={form.email} onChange={set("email")} placeholder="pour rester en lien" className="w-full bg-transparent focus:outline-none" />
                </FormField>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FormField icon={<MapPin className="w-4 h-4" />} label="Ville (optionnel)">
                  <input data-testid="test-city" value={form.city} onChange={set("city")} placeholder="Paris" className="w-full bg-transparent focus:outline-none" />
                </FormField>
                <FormField icon={<Sparkles className="w-4 h-4" />} label="Catégorie">
                  <select data-testid="test-category" value={form.category} onChange={set("category")} className="w-full bg-transparent focus:outline-none">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormField>
              </div>
              <FormField icon={<Heart className="w-4 h-4" />} label="Titre de ton témoignage" required>
                <input required data-testid="test-title" value={form.title} onChange={set("title")} placeholder="Il m'a rencontré dans mon silence" className="w-full bg-transparent focus:outline-none" />
              </FormField>
              <FormField icon={<MessageCircle className="w-4 h-4" />} label="Ton histoire" required multiline>
                <textarea required rows={6} data-testid="test-story" value={form.story} onChange={set("story")}
                  placeholder="Raconte simplement, avec tes mots, ce que Dieu a fait dans ta vie."
                  className="w-full bg-transparent focus:outline-none resize-none" />
              </FormField>

              <p className="text-xs text-[#6B7A85]">
                Ton témoignage sera relu avant publication pour respecter les principes d'édification et de confidentialité.
              </p>

              <button
                disabled={loading}
                type="submit"
                data-testid="submit-testimony"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#4A6D82] text-white hover:bg-[#2E4959] transition disabled:opacity-60 jt-blue-glow"
              >
                <Send className="w-4 h-4" />
                {loading ? "Envoi…" : "Envoyer mon témoignage"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FormField({ icon, label, required, multiline, children }) {
  return (
    <label className="block rounded-2xl border border-[#DDE3E8] bg-[#F7F5F0] px-4 py-3 focus-within:border-[#4A6D82] focus-within:ring-2 focus-within:ring-[#4A6D82]/30 transition">
      <div className="flex items-center gap-2 text-xs text-[#6B7A85] mb-1.5">
        <span className="text-[#4A6D82]">{icon}</span>
        <span className="uppercase tracking-widest">{label}{required && <span className="text-[#C99A5B]"> *</span>}</span>
      </div>
      {children}
    </label>
  );
}
